import { httpsCallable } from 'firebase/functions'
import { functions } from '@/firebase'
import { getSingleGoogleImage } from './GoogleImageSearch'
import unsplashApi from './Unsplash'

interface AICompletionResponse {
  response: {
    content: string
  }
}

interface UnsplashRandomResponse {
  type: 'success'
  response: {
    urls: {
      regular: string
    }
  }[]
}

const createCompletion = httpsCallable<{ chat: any }, AICompletionResponse>(functions, 'createChatCompletion')

export const generateImageSearchQuery = async (prompt: string): Promise<string> => {
  const ai_data = {
    chat: {
      model: 'gpt-4o-mini',
      temperature: 0.7,
      messages: [
        {
          role: 'user',
          content: [
            {
              type: 'text',
              text: `
              Generate a concise search query in English for finding relevant images based on this prompt:
              "${prompt}"

              Rules:
              1. Always return the search query in English, regardless of the input language
              2. For simple objects, use the exact English object name
              3. For complex situations, extract the most visually descriptive elements in English
              4. Focus on concrete, searchable terms that would yield good image results
              5. Keep the query short and simple (2-4 words maximum)
              6. If the prompt is a question, focus on the main subject or concept being asked about
              7. If the prompt is a student response prompt, focus on the topic or subject matter

              Return only the English search query string, nothing else.

              Examples:
              - For "un gato sentado en el alféizar" return: cat on windowsill
              - For "une personne heureuse" return: happy person smiling
              - For "一个复杂的数学方程" return: math equation chalkboard
              - For "What is the capital of France?" return: Paris city skyline
              - For "Describe your favorite animal" return: wild animals nature
              `,
            },
          ],
        },
      ],
    },
  }

  try {
    const { data } = await createCompletion(ai_data)
    return data.response.content.trim()
  } catch (error) {
    console.error('Error generating image search query:', error)
    // Fallback to basic keyword extraction if AI fails
    const words = prompt.toLowerCase().trim().split(' ')
    const keyWords = words.filter(
      word => word.length > 3 && !['the', 'and', 'for', 'with', 'that', 'this', 'are', 'was', 'were'].includes(word),
    )
    return keyWords.slice(0, 3).join(' ')
  }
}

export const getImageFromService = async (
  prompt: string,
  service: 'google' | 'unsplash' = 'unsplash',
): Promise<string | null> => {
  try {
    // Generate the search query using AI
    const searchQuery = await generateImageSearchQuery(prompt)

    if (service === 'google') {
      const image = await getSingleGoogleImage(searchQuery)
      return image?.url || null
    } else if (service === 'unsplash') {
      const response = (await unsplashApi.photos.getRandom({
        query: searchQuery,
        count: 1,
      })) as UnsplashRandomResponse

      if (response.type === 'success') {
        return response.response[0].urls.regular
      }
      return null
    }

    return null
  } catch (error) {
    console.error('Error fetching image:', error)
    return null
  }
} 